<template>
  <v-container>
    <v-row v-if="!selectedPlacementId">
      <!-- Placement Count -->
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text class="data-card">
            <div class="data-card__value-title">Total Placements</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader
                  type="chip"
                  :style="{ paddingBottom: '3px' }"
                />
              </div>
              <div v-else class="data-card__value">
                {{ placements.length }}
              </div>
              <div class="data-card__stat text-subtitle-2">
                <span class="text-medium-emphasis"
                  >Total Number of Placements</span
                >
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Total Placement Loads (flip-able to Total Impressions) -->
      <v-col cols="12" sm="6" md="3">
        <div
          class="flip-card"
          @click="showImpressionsCard = !showImpressionsCard"
          title="Click to flip"
        >
          <div
            class="flip-card-inner"
            :class="{ flipped: showImpressionsCard }"
          >
            <!-- Front: Total Impressions (default) -->
            <v-card class="flip-card-face flip-card-front">
              <v-card-text class="data-card">
                <div class="data-card__value-title">Total Impressions</div>
                <div>
                  <div
                    v-if="isLoading || isWaitingForParams"
                    class="data-card__value-skeleton"
                  >
                    <v-skeleton-loader type="chip" />
                  </div>
                  <div v-else class="data-card__value">
                    {{
                      totalImpressions > 0
                        ? totalImpressions.toLocaleString()
                        : '0'
                    }}
                  </div>
                  <div
                    v-if="isComparedDataLoading"
                    class="data-card__stat-skeleton"
                  >
                    <v-skeleton-loader type="text" />
                  </div>
                  <div v-else class="data-card__stat text-subtitle-2">
                    <div v-if="props.previousStartDate">
                      <template v-if="comparedTotalImpressions === 0">
                        <v-icon
                          v-show="totalImpressions > 0"
                          color="success"
                          size="small"
                          class="mr-1"
                          >mdi-arrow-up</v-icon
                        >
                        <span
                          class="text-success"
                          v-if="totalImpressions > 0"
                          >{{ totalImpressions.toLocaleString() }}</span
                        >
                        <span class="text-medium-emphasis" v-else>0</span>
                      </template>
                      <template v-else>
                        <v-icon
                          :color="impressionsChange >= 0 ? 'success' : 'error'"
                          size="small"
                          class="mr-1"
                          v-show="comparedTotalImpressions > 0"
                        >
                          {{
                            impressionsChange >= 0
                              ? 'mdi-arrow-up'
                              : 'mdi-arrow-down'
                          }}
                        </v-icon>
                        <v-tooltip
                          location="top"
                          :text="`Percentage change from previous period: ${impressionsChange >= 0 ? '+' : '-'}${Math.abs(impressionsChange).toFixed(2)}%`"
                        >
                          <template v-slot:activator="{ props }">
                            <span
                              v-bind="props"
                              :class="
                                impressionsChange >= 0
                                  ? 'text-success'
                                  : 'text-error'
                              "
                              style="cursor: help"
                            >
                              {{ Math.abs(impressionsChange).toFixed(2) }}%
                            </span>
                          </template>
                        </v-tooltip>
                      </template>
                      <v-tooltip
                        location="top"
                        :text="`Previous period value: ${comparedTotalImpressions.toLocaleString()} impressions`"
                      >
                        <template v-slot:activator="{ props }">
                          <span
                            v-bind="props"
                            class="data-card__compared-value"
                            style="cursor: help"
                          >
                            ({{ comparedTotalImpressions }})
                          </span>
                        </template>
                      </v-tooltip>
                    </div>
                    <div v-else class="text-medium-emphasis">
                      <span>Total Number of Placement Impressions</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Back: Total Loads -->
            <v-card class="flip-card-face flip-card-back">
              <v-card-text class="data-card">
                <div class="data-card__value-title">Total Loads</div>
                <div>
                  <div
                    v-if="isLoading || isWaitingForParams"
                    class="data-card__value-skeleton"
                  >
                    <v-skeleton-loader type="chip" />
                  </div>
                  <div v-else class="data-card__value">
                    {{ totalLoads > 0 ? totalLoads.toLocaleString() : '0' }}
                  </div>
                  <div
                    v-if="isComparedDataLoading"
                    class="data-card__stat-skeleton"
                  >
                    <v-skeleton-loader type="text" />
                  </div>
                  <div v-else class="data-card__stat text-subtitle-2">
                    <div v-if="props.previousStartDate">
                      <template v-if="comparedTotalLoads === 0">
                        <v-icon
                          v-show="totalLoads > 0"
                          color="success"
                          size="small"
                          class="mr-1"
                          >mdi-arrow-up</v-icon
                        >
                        <span class="text-success" v-if="totalLoads > 0">{{
                          totalLoads.toLocaleString()
                        }}</span>
                        <span class="text-medium-emphasis" v-else>0</span>
                      </template>
                      <template v-else>
                        <v-icon
                          :color="loadChange >= 0 ? 'success' : 'error'"
                          size="small"
                          class="mr-1"
                          v-show="comparedTotalLoads > 0"
                        >
                          {{
                            loadChange >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
                          }}
                        </v-icon>
                        <v-tooltip
                          location="top"
                          :text="`Percentage change from previous period: ${loadChange >= 0 ? '+' : '-'}${Math.abs(loadChange).toFixed(2)}%`"
                        >
                          <template v-slot:activator="{ props }">
                            <span
                              v-bind="props"
                              :class="
                                loadChange >= 0 ? 'text-success' : 'text-error'
                              "
                              style="cursor: help"
                            >
                              {{ Math.abs(loadChange).toFixed(2) }}%
                            </span>
                          </template>
                        </v-tooltip>
                      </template>
                      <v-tooltip
                        location="top"
                        :text="`Previous period value: ${comparedTotalLoads.toLocaleString()} loads`"
                      >
                        <template v-slot:activator="{ props }">
                          <span
                            v-bind="props"
                            class="data-card__compared-value"
                            style="cursor: help"
                          >
                            ({{ comparedTotalLoads }})
                          </span>
                        </template>
                      </v-tooltip>
                    </div>
                    <div v-else class="text-medium-emphasis">
                      <span>Total Number of Times Placements Loaded</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-col>

      <!-- Total Clicks -->
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text class="data-card">
            <div class="text-subtitle-1 text-medium-emphasis">Total Clicks</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader type="chip" />
              </div>
              <div
                v-else
                class="data-card__value"
                :style="{ paddingTop: '2px' }"
              >
                <span>{{ totalClicks }}</span>
              </div>
              <div
                v-if="isComparedDataLoading"
                class="data-card__stat-skeleton"
              >
                <v-skeleton-loader type="text" />
              </div>
              <div v-else class="data-card__stat">
                <div v-if="props.previousStartDate">
                  <template v-if="comparedTotalClicks === 0">
                    <v-icon
                      v-show="totalClicks > 0"
                      color="success"
                      size="small"
                      class="mr-1"
                      >mdi-arrow-up</v-icon
                    >
                    <span class="text-success" v-if="totalClicks > 0">{{
                      totalClicks.toLocaleString()
                    }}</span>
                    <span class="text-medium-emphasis" v-else>0</span>
                  </template>
                  <template v-else>
                    <v-icon
                      :color="clicksChange >= 0 ? 'success' : 'error'"
                      size="small"
                      class="mr-1"
                      v-show="comparedTotalClicks > 0"
                    >
                      {{
                        clicksChange >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
                      }}
                    </v-icon>
                    <v-tooltip
                      location="top"
                      :text="`Percentage change from previous period: ${clicksChange >= 0 ? '+' : '-'}${Math.abs(clicksChange).toFixed(2)}%`"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          :class="
                            clicksChange >= 0 ? 'text-success' : 'text-error'
                          "
                          style="cursor: help"
                        >
                          {{ Math.abs(clicksChange).toFixed(2) }}%
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-tooltip
                    location="top"
                    :text="`Previous period value: ${comparedTotalClicks.toLocaleString()} clicks`"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="data-card__compared-value"
                        style="cursor: help"
                      >
                        ({{ comparedTotalClicks }})
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div v-else class="text-medium-emphasis">
                  <span>Total Number of Times Vehicles were Clicked</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Click Through Rate -->
      <v-col cols="12" sm="6" md="3">
        <v-card>
          <v-card-text class="data-card">
            <div class="text-subtitle-1 text-medium-emphasis">Total CTR</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader type="chip" />
              </div>
              <div v-else class="data-card__value">
                <span>{{ ctr.toFixed(2) }}%</span>
              </div>
              <div
                v-if="isComparedDataLoading"
                class="data-card__stat-skeleton"
              >
                <v-skeleton-loader type="text" />
              </div>
              <div v-else class="data-card__stat text-subtitle-2">
                <div v-if="props.previousStartDate">
                  <template v-if="comparedCtr === 0">
                    <v-icon
                      v-show="ctr > 0"
                      color="success"
                      size="small"
                      class="mr-1"
                      >mdi-arrow-up</v-icon
                    >
                    <span class="text-success" v-if="ctr > 0"
                      >{{ ctr.toFixed(2) }}%</span
                    >
                    <span class="text-medium-emphasis" v-else>0.00%</span>
                  </template>
                  <template v-else>
                    <v-icon
                      :color="ctrChange >= 0 ? 'success' : 'error'"
                      size="small"
                      class="mr-1"
                      v-show="comparedCtr > 0"
                    >
                      {{ ctrChange >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    <v-tooltip
                      location="top"
                      :text="`Percentage change from previous period: ${ctrChange >= 0 ? '+' : '-'}${Math.abs(ctrChange).toFixed(2)}%`"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          :class="
                            ctrChange >= 0 ? 'text-success' : 'text-error'
                          "
                          style="cursor: help"
                        >
                          {{ Math.abs(ctrChange).toFixed(2) }}%
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-tooltip
                    location="top"
                    :text="`Previous period value: ${comparedCtr > 0 ? Math.abs(comparedCtr).toFixed(2) + '%' : '0%'} CTR`"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="data-card__compared-value"
                        style="cursor: help"
                      >
                        ({{
                          comparedCtr > 0
                            ? Math.abs(comparedCtr).toFixed(2) + '%'
                            : '0'
                        }})
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div v-else class="text-medium-emphasis">
                  <span>Total Click-through Rate</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Placements Analytics -->
      <v-col cols="12">
        <v-card class="data-list">
          <v-card-text>
            <!-- Add Placement Button above table -->
            <div
              class="d-flex justify-end mb-3"
              v-if="!selectedPlacementId && hasEditAccess"
            >
              <v-btn
                data-testid="add-placement-btn"
                color="grey-darken-1"
                variant="text"
                size="small"
                @click="$emit('addPlacement')"
                prepend-icon="mdi-plus-circle"
                class="text-caption"
              >
                Add Placement
              </v-btn>
            </div>
            <v-data-table
              :headers="tableHeaders"
              :items="sortedAnalytics"
              :items-per-page="itemsPerPage"
              :loading="isTableLoading"
              height="400px"
              fixed-header
              hover
              :page="page"
              :items-per-page-options="[5, 8, 10]"
              :items-length="totalItems"
              :server-items-length="totalItems"
              :custom-key-sort="customKeySort"
              :sort-by="currentSort"
              item-value="id"
            >
              <template v-slot:item="{ item }">
                <tr
                  @click="handleRowClick(item)"
                  class="cursor-pointer"
                  :class="{
                    'disabled-placement-row': isPlacementDisabled(item),
                    'non-clickable-row': !hasEditAccess,
                  }"
                  :title="
                    !hasEditAccess
                      ? 'Admin access required to edit placement'
                      : ''
                  "
                >
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{ item.name }}</span>
                      <span
                        v-if="isPlacementDisabled(item)"
                        class="text-caption text-medium-emphasis ml-2"
                      >
                        (Disabled)
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">
                        {{
                          !showImpressionsCard
                            ? getAggregatedValue(item.viewCount || '0')
                            : getAggregatedValue(item.loadCount)
                        }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{
                        getAggregatedValue(item.clickCount)
                      }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{
                        formatPct(item.ctrComputed)
                      }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center justify-center">
                      <v-menu location="bottom end">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            size="small"
                            variant="text"
                            color="grey-darken-1"
                            v-bind="props"
                            class="text-caption text-medium-emphasis"
                            :aria-label="`Actions for ${item.name}`"
                          >
                            <v-icon size="small" class="mr-1"
                              >mdi-dots-vertical</v-icon
                            >
                            <span class="d-none d-sm-inline-block"
                              >Actions</span
                            >
                          </v-btn>
                        </template>

                        <v-list density="compact">
                          <!-- View Placement -->
                          <v-list-item
                            v-if="item.url"
                            @click="openUrl(item.url)"
                            class="mb-1"
                            density="compact"
                          >
                            <template v-slot:prepend>
                              <v-icon size="small" color="primary" class="mr-2"
                                >mdi-open-in-new</v-icon
                              >
                            </template>
                            <v-list-item-title
                              class="text-body-2 text-medium-emphasis"
                            >
                              View Placement
                            </v-list-item-title>
                          </v-list-item>

                          <!-- No URL message when URL is not available -->
                          <v-list-item
                            v-else
                            class="mb-1"
                            density="compact"
                            disabled
                          >
                            <template v-slot:prepend>
                              <v-icon size="small" color="grey" class="mr-2"
                                >mdi-link-off</v-icon
                              >
                            </template>
                            <v-list-item-title
                              class="text-body-2 text-medium-emphasis"
                            >
                              No URL Available
                            </v-list-item-title>
                          </v-list-item>

                          <v-divider class="my-2"></v-divider>

                          <!-- Copy Embed Code -->
                          <v-list-item
                            @click="copyEmbedCode(item.id)"
                            class="mb-1"
                            density="compact"
                          >
                            <template v-slot:prepend>
                              <v-icon
                                size="small"
                                color="grey-darken-1"
                                class="mr-2"
                                >mdi-content-copy</v-icon
                              >
                            </template>
                            <v-list-item-title
                              class="text-body-2 text-medium-emphasis"
                            >
                              {{
                                copyStatus === item.id
                                  ? 'Copied!'
                                  : 'Copy Embed Code'
                              }}
                            </v-list-item-title>
                          </v-list-item>

                          <!-- Validate Schema -->
                          <v-list-item
                            @click="openValidateSchema(item.id)"
                            class="mb-1"
                            density="compact"
                          >
                            <template v-slot:prepend>
                              <v-icon
                                size="small"
                                color="grey-darken-1"
                                class="mr-2"
                                >mdi-google</v-icon
                              >
                            </template>
                            <v-list-item-title
                              class="text-body-2 text-medium-emphasis"
                            >
                              Validate Schema
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Validate Schema Dialog -->
  <ValidateSchemaDialog
    v-model="showValidateSchemaDialog"
    :content="validationUrl"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { calculatePercentageChange, formatDate } from '@/stores/utils'
import type { IPlacementAnalytic } from '@/components/dashboard/types'
import type { IPlacements } from '@/types'
import type { IMustAnalytics } from '@/types'
import { getMustComparedAnalytics } from '@/components/dashboard/utils'
import type { IPlacementAnalytics } from '@/components/dashboard/types'
import { normalizeDeviceType } from '@/utils'
import ValidateSchemaDialog from '@/components/ValidateSchemaDialog.vue'
import { config } from '@/config/generated-config'

const getDeviceTypeIcon = (
  deviceType: string,
): { icon: string; tooltip: string } => {
  const normalizedType = normalizeDeviceType(deviceType)

  switch (normalizedType) {
    case 'desktop':
      return { icon: 'mdi-monitor', tooltip: 'Desktop' }
    case 'mobile':
      return { icon: 'mdi-cellphone', tooltip: 'Mobile' }
    case 'tablet':
      return { icon: 'mdi-tablet', tooltip: 'Tablet' }
    case 'unknown':
      return { icon: 'mdi-help-circle', tooltip: 'Unknown' }
    default:
      console.log('Falling to default case for deviceType:', deviceType)
      return { icon: 'mdi-help-circle', tooltip: 'Unknown' }
  }
}

// Helper function to create default device data for all device types
const createDefaultDeviceData = (value: string): string => {
  const deviceTypes = ['mobile', 'desktop', 'tablet', 'unknown']
  const deviceData = deviceTypes.map(deviceType => {
    const deviceIcon = getDeviceTypeIcon(deviceType)
    return `${deviceIcon.icon}:${deviceIcon.tooltip}:${value}`
  })
  return deviceData.join('|')
}

// Helper function to sort device data in the desired order
const sortDeviceData = (dataString: string): string => {
  if (!dataString || dataString === '') return ''

  const deviceOrder = ['mobile', 'desktop', 'tablet', 'unknown']
  const deviceMap = new Map()

  // Parse existing data
  dataString.split('|').forEach(item => {
    const [icon, tooltip, value] = item.split(':')
    const deviceType = tooltip.toLowerCase()
    deviceMap.set(deviceType, { icon, tooltip, value })
  })

  // Build sorted result
  const sortedDevices: string[] = []

  // First, add devices in the desired order
  deviceOrder.forEach(deviceType => {
    if (deviceMap.has(deviceType)) {
      const device = deviceMap.get(deviceType)
      sortedDevices.push(`${device.icon}:${device.tooltip}:${device.value}`)
    }
  })

  // Then, add any remaining devices that weren't in the predefined order
  deviceMap.forEach((device, deviceType) => {
    if (!deviceOrder.includes(deviceType)) {
      sortedDevices.push(`${device.icon}:${device.tooltip}:${device.value}`)
    }
  })

  return sortedDevices.join('|')
}

const getAggregatedNumber = (dataString: string): number => {
  if (!dataString || dataString === '0') return 0

  // respect device-type filters (same logic as getAggregatedValue)
  if (props.deviceTypeFilters && props.deviceTypeFilters.length > 0) {
    return dataString.split('|').reduce((sum, item) => {
      const parts = item.split(':')
      const tooltip = (parts[1] || '').toLowerCase()
      const value = parseFloat(parts[2] || '0') || 0
      return props.deviceTypeFilters!.includes(tooltip) ? sum + value : sum
    }, 0)
  }

  // if filters are explicitly empty, show 0
  if (props.deviceTypeFilters && props.deviceTypeFilters.length === 0) return 0

  // no filters -> sum all
  return dataString
    .split('|')
    .map(i => parseFloat(i.split(':')[2] || '0') || 0)
    .reduce((a, b) => a + b, 0)
}

const computeCtrFromData = (clicksStr: string, viewsStr: string): number => {
  const clicks = getAggregatedNumber(clicksStr)
  const views = getAggregatedNumber(viewsStr)
  if (!views) return 0
  return (clicks / views) * 100
}

const formatPct = (n: number, digits = 2): string => `${n.toFixed(digits)}%`

const parseDeviceData = (
  dataString: string,
): Array<{ icon: string; tooltip: string; value: string }> => {
  if (!dataString || dataString === '0') return []

  const deviceData = dataString
    .split('|')
    .map(item => {
      const parts = item.split(':')
      const icon = parts[0] || ''
      const tooltip = parts[1] || ''
      const value = parts[2] || ''
      return { icon, tooltip, value }
    })
    .filter(item => item.icon && item.tooltip && item.value) // Only include complete items

  // Filter by device type if filters are provided
  if (props.deviceTypeFilters && props.deviceTypeFilters.length > 0) {
    return deviceData.filter(item => {
      // Add null check for tooltip
      if (!item.tooltip) return false
      const deviceType = item.tooltip.toLowerCase()
      return props.deviceTypeFilters!.includes(deviceType)
    })
  }

  // If no device type filters are provided, return empty array (show no data)
  return []
}

// Simple helper to format CTR values with max 2 decimal places
const formatCTR = (value: string): string => {
  if (value === '0') return '0.00%'
  const numValue = parseFloat(value.replace('%', ''))
  return isNaN(numValue) ? '0.00%' : numValue.toFixed(2) + '%'
}

const getAggregatedValue = (dataString: string): string => {
  if (!dataString || dataString === '0') return '0'

  // Parse the device data string and sum up all values
  const deviceData = dataString
    .split('|')
    .map(item => {
      const parts = item.split(':')
      const value = parts[2] || '0'
      return parseFloat(value) || 0
    })
    .filter(value => !isNaN(value))

  // If device type filters are applied, only include selected device types
  if (props.deviceTypeFilters && props.deviceTypeFilters.length > 0) {
    const filteredData = dataString
      .split('|')
      .map(item => {
        const parts = item.split(':')
        const tooltip = parts[1] || ''
        const value = parts[2] || '0'
        const deviceType = tooltip.toLowerCase()

        if (props.deviceTypeFilters!.includes(deviceType)) {
          return parseFloat(value) || 0
        }
        return 0
      })
      .filter(value => !isNaN(value))

    const total = filteredData.reduce((sum, value) => sum + value, 0)
    return total.toString()
  }

  // If no device types are selected (empty array), show 0
  if (props.deviceTypeFilters && props.deviceTypeFilters.length === 0) {
    return '0'
  }

  // If no filters provided (undefined), sum all values (default behavior)
  const total = deviceData.reduce((sum, value) => sum + value, 0)
  return total.toString()
}

const props = defineProps<{
  accountId: string
  accountName: string
  placements: IPlacements
  placementsCount: string
  startDate?: Date
  endDate?: Date
  previousStartDate?: Date | null
  previousEndDate?: Date | null
  isReady: boolean
  deviceTypeFilters?: string[]
  hasEditAccess: boolean
}>()

const emit = defineEmits<{
  'update:placementId': [placementId: string]
  'update:deviceTypeFilters': [deviceType: string[]]
  addPlacement: []
}>()

const totalItems = ref(0)
const itemsPerPage = ref(10)
const page = ref(1)
const selectedPlacementId = ref<string | null>(null)
const isLoading = ref(false)
const isWaitingForParams = ref(false)
const isComparedDataLoading = ref(false)
const isTableLoading = ref(false)

// State for copy embed code functionality
const copyStatus = ref<string | null>(null)
const showValidateSchemaDialog = ref(false)
const selectedPlacementForValidation = ref<string | null>(null)

const placements = computed((): IPlacementAnalytic[] => {
  return Object.entries(props.placements)
    .map(([, value], index) => {
      const key = Object.keys(props.placements)[index]
      if (key.startsWith('preview__')) {
        return null as IPlacementAnalytic | null
      }

      return {
        id: key,
        name: value.friendlyName,
        url: value.url || '',
        loadCount: '0',
        viewCount: '0',
        clickCount: '0',
        ctr: '0%',
        ctrComputed: 0,
      } as IPlacementAnalytic
    })
    .filter((item): item is IPlacementAnalytic => item !== null)
})

// Computed property for validation URL
const validationUrl = computed(() => {
  if (!selectedPlacementForValidation.value) return ''

  const cxUrl = config.VITE_CX_URL || ''
  return `${cxUrl}?account_id=${props.accountName}&placement_id=${selectedPlacementForValidation.value}`
})

const analytics = ref<IPlacementAnalytic[] | null>(placements.value)
const sortedAnalytics = computed(() => {
  const items = analytics.value ?? []
  return [...items].sort((a, b) => a.name.localeCompare(b.name))
})
// add next to your other consts in <script setup>
const customKeySort: Record<string, (a: any, b: any) => number> = {
  name: (a, b) =>
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    }),

  loadCount: (a, b) =>
    parseFloat(getAggregatedValue(a)) - parseFloat(getAggregatedValue(b)),

  viewCount: (a, b) =>
    parseFloat(getAggregatedValue(a)) - parseFloat(getAggregatedValue(b)),

  clickCount: (a, b) =>
    parseFloat(getAggregatedValue(a)) - parseFloat(getAggregatedValue(b)),

  ctrComputed: (a, b) => (Number(a) || 0) - (Number(b) || 0),
}

// replace your TableHeader + tableHeaders with this
interface TableHeader {
  title: string
  key:
    | 'name'
    | 'loadCount'
    | 'viewCount'
    | 'clickCount'
    | 'ctrComputed'
    | 'actions'
  align?: 'start' | 'end' | 'center'
  sortable?: boolean
}

const tableHeaders = computed<TableHeader[]>(() => [
  { title: 'Placement Name', key: 'name', align: 'start' },
  !showImpressionsCard.value
    ? { title: 'Impressions', key: 'viewCount', align: 'start' }
    : { title: 'Loads', key: 'loadCount', align: 'start' },
  { title: 'Clicks', key: 'clickCount', align: 'start' },
  { title: 'CTR', key: 'ctrComputed', align: 'start' },
  { title: 'Actions', key: 'actions', align: 'center', sortable: false },
])

// Dynamic sort: default to impressions; switch to loads when flipped
const currentSort = computed(() => {
  return [
    {
      key: !showImpressionsCard.value
        ? ('viewCount' as const)
        : ('loadCount' as const),
      order: 'desc' as const,
    },
  ]
})

// Views
const totalLoads = ref(0)
const comparedTotalLoads = ref(0)
const loadChange = ref(0)

// Click-through rate
const ctr = ref(0)
const comparedCtr = ref(0)
const ctrChange = ref(0)

// Clicks
const totalClicks = ref(0)
const comparedTotalClicks = ref(0)
const clicksChange = ref(0)

// Impressions (placement views)
const totalImpressions = ref(0)
const comparedTotalImpressions = ref(0)
const impressionsChange = ref(0)

// Flip state for Loads <-> Impressions card
const showImpressionsCard = ref(false)

// Cache the last fetched analytics so we can recompute metrics on
// device-type filter changes without refetching from the backend
const lastCurrentData = ref<IMustAnalytics | null>(null)
const lastPreviousData = ref<IMustAnalytics | null>(null)

const handleComparedPlacementLoadDataChange = async (
  productId: string,
  startDate: string,
  endDate: string,
) => {
  const formattedPreviousStartDate = formatDate(
    props.previousStartDate ?? new Date(),
  )
  const formattedPreviousEndDate = formatDate(
    props.previousEndDate ?? new Date(),
  )
  const activePlacements = placements.value.map(item => item.id)

  // Get placement load data - simplified to only use working dimensions
  const placementLoadAnalytics = await getMustComparedAnalytics(
    props.accountId,
    props.accountName,
    productId,
    ['placement', 'deviceType'],
    ['_events', 'eventType', 'source', 'account'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'placement_load',
      },
    ],
    startDate,
    endDate,
    formattedPreviousStartDate,
    formattedPreviousEndDate,
    activePlacements,
  )

  // Get vehicle click data
  const vehicleClickAnalytics = await getMustComparedAnalytics(
    props.accountId,
    props.accountName,
    productId,
    ['placement', 'deviceType'],
    ['_events', 'eventType', 'source', 'account'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'vehicle_click',
      },
    ],
    startDate,
    endDate,
    formattedPreviousStartDate,
    formattedPreviousEndDate,
    activePlacements,
  )

  // Get placement view (impressions) data
  const placementViewAnalytics = await getMustComparedAnalytics(
    props.accountId,
    props.accountName,
    productId,
    ['placement', 'deviceType'],
    ['_events', 'eventType', 'source', 'account'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'placement_view',
      },
    ],
    startDate,
    endDate,
    formattedPreviousStartDate,
    formattedPreviousEndDate,
    activePlacements,
  )

  const currentPeriodData: any = {
    loads: placementLoadAnalytics.current,
    clicks: vehicleClickAnalytics.current,
    views: placementViewAnalytics.current,
  }

  const previousPeriodData: any = {
    loads: placementLoadAnalytics.previous,
    clicks: vehicleClickAnalytics.previous,
    views: placementViewAnalytics.previous,
  }

  const placementDeviceMap = new Map<
    string,
    {
      loads: Record<string, number>
      clicks: Record<string, number>
      views: Record<string, number>
    }
  >()

  // Aggregate loads by placement and deviceType
  ;(placementLoadAnalytics?.current?.items ?? []).forEach(item => {
    const placement = item.dimensions?.placement
    const rawDeviceType = item.dimensions?.deviceType || 'Unknown'
    const deviceType = normalizeDeviceType(rawDeviceType)
    const loadCount = item.metrics?.['_events'] ?? 0

    if (!placementDeviceMap.has(placement)) {
      placementDeviceMap.set(placement, { loads: {}, clicks: {}, views: {} })
    }

    const placementData = placementDeviceMap.get(placement)!
    // Aggregate counts for the same normalized device type
    placementData.loads[deviceType] =
      (placementData.loads[deviceType] || 0) + loadCount
  })

  // Aggregate clicks by placement and deviceType
  ;(vehicleClickAnalytics?.current?.items ?? []).forEach(item => {
    const placement = item.dimensions?.placement
    const rawDeviceType = item.dimensions?.deviceType || 'Unknown'
    const deviceType = normalizeDeviceType(rawDeviceType)
    const clickCount = item.metrics?.['_events'] ?? 0

    if (!placementDeviceMap.has(placement)) {
      placementDeviceMap.set(placement, { loads: {}, clicks: {}, views: {} })
    }
    placementDeviceMap.get(placement)!.clicks[deviceType] =
      (placementDeviceMap.get(placement)!.clicks[deviceType] || 0) + clickCount
  })

  // Aggregate views by placement and deviceType
  ;(placementViewAnalytics?.current?.items ?? []).forEach(item => {
    const placement = item.dimensions?.placement
    const rawDeviceType = item.dimensions?.deviceType || 'Unknown'
    const deviceType = normalizeDeviceType(rawDeviceType)
    const viewCount = item.metrics?.['_events'] ?? 0

    if (!placementDeviceMap.has(placement)) {
      placementDeviceMap.set(placement, { loads: {}, clicks: {}, views: {} })
    }

    placementDeviceMap.get(placement)!.views[deviceType] =
      (placementDeviceMap.get(placement)!.views[deviceType] || 0) + viewCount
  })

  // Build the final data array
  const placementData: IPlacementAnalytics[] = []
  placementDeviceMap.forEach((deviceData, placement) => {
    const loadStrArr: string[] = []
    const viewStrArr: string[] = []
    const clickStrArr: string[] = []
    const ctrStrArr: string[] = []

    const allDeviceTypes = new Set([
      ...Object.keys(deviceData.loads),
      ...Object.keys(deviceData.clicks),
      ...Object.keys(deviceData.views),
    ])

    allDeviceTypes.forEach(deviceType => {
      const loads = deviceData.loads[deviceType] || 0
      const clicks = deviceData.clicks[deviceType] || 0
      const views = deviceData.views[deviceType] || 0
      const ctr = views ? ((clicks / views) * 100).toFixed(2) : '0.00'
      const deviceIcon = getDeviceTypeIcon(deviceType)

      loadStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${loads}`)
      viewStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${views}`)
      clickStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${clicks}`)
      ctrStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${ctr}%`)
    })

    placementData.push({
      placement,
      loads: sortDeviceData(loadStrArr.join('|')),
      views: sortDeviceData(viewStrArr.join('|')),
      clicks: sortDeviceData(clickStrArr.join('|')),
      ctr: sortDeviceData(ctrStrArr.join('|')),
      url: '',
      ctrComputed: computeCtrFromData(
        clickStrArr.join('|'),
        viewStrArr.join('|'),
      ),
    })
  })

  updatePlacementsData(placementData)
  // Cache results for recomputation when device filters change
  lastCurrentData.value = currentPeriodData
  lastPreviousData.value = previousPeriodData
  updateMetrics(currentPeriodData, previousPeriodData)
}

const handleDateChange = async () => {
  try {
    isLoading.value = true
    isComparedDataLoading.value = true
    isTableLoading.value = true

    const productId = 'micro_srp'
    const formattedStartDate = formatDate(props.startDate ?? new Date())
    const formattedEndDate = formatDate(props.endDate ?? new Date())

    await handleComparedPlacementLoadDataChange(
      productId,
      formattedStartDate,
      formattedEndDate,
    )
  } catch (error) {
    console.error('micro_srp', 'handleDateChange', error)
  } finally {
    isComparedDataLoading.value = false
    isLoading.value = false
    isTableLoading.value = false
  }
}

const handlePartialDataChange = async () => {
  try {
    isComparedDataLoading.value = true

    const productId = 'micro_srp'
    const formattedStartDate = formatDate(props.startDate ?? new Date())
    const formattedEndDate = formatDate(props.endDate ?? new Date())

    await handleComparedPlacementLoadDataChange(
      productId,
      formattedStartDate,
      formattedEndDate,
    )
  } catch (error) {
    console.error('Error fetching analytics data:', error)
  } finally {
    isComparedDataLoading.value = false
  }
}

// Helpers to apply device type filtering to summary metrics
const shouldIncludeDeviceType = (raw: string | undefined): boolean => {
  const filters = props.deviceTypeFilters
  if (filters === undefined) return true
  if (filters.length === 0) return false
  return filters.includes(normalizeDeviceType(raw || 'unknown'))
}

const sumEvents = (items: any[] = []) =>
  items
    .filter(item => shouldIncludeDeviceType(item?.dimensions?.deviceType))
    .reduce((sum, item) => sum + (item?.metrics?.['_events'] ?? 0), 0)

const updateMetrics = (
  currentData: IMustAnalytics | null,
  previousData: IMustAnalytics | null,
) => {
  // If no placements are selected in the filter, clear all metrics
  if (props.placementsCount === '0') {
    totalLoads.value = 0
    comparedTotalLoads.value = 0
    loadChange.value = 0
    totalClicks.value = 0
    comparedTotalClicks.value = 0
    clicksChange.value = 0
    ctr.value = 0
    comparedCtr.value = 0
    ctrChange.value = 0
    return
  }

  // Update placement loads (respect device type filters)
  const currentLoads = sumEvents(currentData?.loads?.items)
  const previousLoads = sumEvents(previousData?.loads?.items)
  totalLoads.value = currentLoads
  comparedTotalLoads.value = previousLoads
  loadChange.value =
    comparedTotalLoads.value === 0
      ? 0
      : calculatePercentageChange(totalLoads.value, comparedTotalLoads.value)

  // Update vehicle clicks
  const currentClicks = sumEvents(currentData?.clicks?.items)
  const previousClicks = sumEvents(previousData?.clicks?.items)
  totalClicks.value = currentClicks
  comparedTotalClicks.value = previousClicks
  clicksChange.value =
    comparedTotalClicks.value === 0
      ? 0
      : calculatePercentageChange(totalClicks.value, comparedTotalClicks.value)

  // Update placement impressions (views)
  const currentViews = sumEvents(currentData?.views?.items)
  const previousViews = sumEvents(previousData?.views?.items)
  totalImpressions.value = currentViews
  comparedTotalImpressions.value = previousViews
  impressionsChange.value =
    comparedTotalImpressions.value === 0
      ? 0
      : calculatePercentageChange(
          totalImpressions.value,
          comparedTotalImpressions.value,
        )

  // Update CTR using impressions (views)
  let currentCtr = currentViews === 0 ? 0 : (currentClicks / currentViews) * 100
  let previousCtr =
    previousViews === 0 ? 0 : (previousClicks / previousViews) * 100

  if (isNaN(currentCtr)) {
    currentCtr = 0
  }

  if (isNaN(previousCtr)) {
    previousCtr = 0
  }

  ctr.value = Number(currentCtr.toFixed(2))
  comparedCtr.value = Number(previousCtr.toFixed(2))
  ctrChange.value = calculatePercentageChange(ctr.value, comparedCtr.value)
}

const updatePlacementsData = (placementsData: IPlacementAnalytics[]) => {
  // If no placements are selected in the filter, clear the table
  if (props.placementsCount === '0') {
    analytics.value = []
    totalItems.value = 0
    return
  }

  const updatedAnalytics: IPlacementAnalytic[] = []

  for (const placement of placements.value) {
    const analyticsForPlacement: IPlacementAnalytics | undefined = (
      placementsData ?? []
    ).find((item: any) => item.placement === placement.id)

    // Always start with default data (all zeros)
    let loadData = createDefaultDeviceData('0')
    let viewData = createDefaultDeviceData('0')
    let clickData = createDefaultDeviceData('0')
    let ctrData = createDefaultDeviceData('0%')

    // If we have analytics data, merge it with the default data
    if (analyticsForPlacement) {
      if (analyticsForPlacement.loads) {
        loadData = mergeDeviceData(loadData, analyticsForPlacement.loads)
      }
      if (analyticsForPlacement.views) {
        viewData = mergeDeviceData(viewData, analyticsForPlacement.views)
      }
      if (analyticsForPlacement.clicks) {
        clickData = mergeDeviceData(clickData, analyticsForPlacement.clicks)
      }
      if (analyticsForPlacement.ctr) {
        ctrData = mergeDeviceData(ctrData, analyticsForPlacement.ctr)
      }
    }

    updatedAnalytics.push({
      name: placement.name,
      id: placement.id,
      loadCount: loadData,
      viewCount: viewData,
      clickCount: clickData,
      ctr: ctrData,
      ctrComputed: computeCtrFromData(clickData, viewData),
      url: placement.url || '',
    })
  }

  analytics.value = updatedAnalytics
}

// Helper function to merge analytics data with default data
const mergeDeviceData = (
  defaultData: string,
  analyticsData: string,
): string => {
  if (!analyticsData || analyticsData === '0') return defaultData

  const defaultMap = new Map()
  const analyticsMap = new Map()

  // Parse default data
  defaultData.split('|').forEach(item => {
    const [icon, tooltip, value] = item.split(':')
    defaultMap.set(tooltip.toLowerCase(), { icon, tooltip, value })
  })

  // Parse analytics data
  analyticsData.split('|').forEach(item => {
    const [icon, tooltip, value] = item.split(':')
    analyticsMap.set(tooltip.toLowerCase(), { icon, tooltip, value })
  })

  // Merge: use analytics data if available, otherwise use default
  const deviceTypes = ['mobile', 'desktop', 'tablet', 'unknown']
  const mergedData = deviceTypes
    .map(deviceType => {
      const analyticsItem = analyticsMap.get(deviceType)
      const defaultItem = defaultMap.get(deviceType)

      if (analyticsItem) {
        return `${analyticsItem.icon}:${analyticsItem.tooltip}:${analyticsItem.value}`
      } else if (defaultItem) {
        return `${defaultItem.icon}:${defaultItem.tooltip}:${defaultItem.value}`
      }
      return ''
    })
    .filter(item => item !== '')

  return mergedData.join('|')
}

// Helper function to check if placement is disabled

const isPlacementDisabled = (item: IPlacementAnalytic) => {
  const placement = props.placements[item.id]
  return placement && placement.enabled === false
}

const handleRowClick = (row: IPlacementAnalytic) => {
  // Only allow admin users to navigate to placement edit page
  if (!props.hasEditAccess) return

  selectedPlacementId.value = row.id
  emit('update:placementId', row.id)
}

// Add this function to handle opening URLs
const openUrl = (url: string) => {
  if (url && url.trim()) {
    let fullUrl = url.trim()

    // Add protocol if missing
    if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
      fullUrl = `https://${fullUrl}`
    }

    window.open(fullUrl, '_blank', 'noopener,noreferrer')
  }
}

// Function to copy embed code for a placement
const copyEmbedCode = async (placementId: string) => {
  try {
    const placement = props.placements[placementId]
    if (!placement) return

    const cdn = config.VITE_CDN_URL || ''
    const content = `<script src='${cdn}/micro-srp/cx/loader.js'><\/script><div class='micro-srp' data-account-id='${props.accountName}' data-placement-id='${placementId}'></div>`

    await navigator.clipboard.writeText(content)
    copyStatus.value = placementId

    // Reset the status after 2 seconds
    setTimeout(() => {
      copyStatus.value = null
    }, 2000)
  } catch (err) {
    console.error('Failed to copy embed code:', err)
  }
}

// Function to open validate schema dialog
const openValidateSchema = (placementId: string) => {
  selectedPlacementForValidation.value = placementId
  showValidateSchemaDialog.value = true
}

const handleDeviceTypeChange = (deviceType: string) => {
  // This function is no longer used since device type selection is now handled in Dashboard
  // Keeping for backward compatibility
  emit('update:deviceTypeFilters', [deviceType])
}

onMounted(() => {
  if (Object.keys(props.placements).length === 0) {
    isLoading.value = false
    isTableLoading.value = false
    isWaitingForParams.value = false
  }

  handleDateChange()
})

watch(
  () => [props.placements],
  ([placements]) => {
    if (placements) {
      handleDateChange()
    }
  },
)

// Watch placementsCount to immediately clear table and metrics when filter is cleared
watch(
  () => props.placementsCount,
  newCount => {
    if (newCount === '0') {
      analytics.value = []
      totalItems.value = 0
      // Clear all summary metrics
      totalLoads.value = 0
      comparedTotalLoads.value = 0
      loadChange.value = 0
      totalClicks.value = 0
      comparedTotalClicks.value = 0
      clicksChange.value = 0
      totalImpressions.value = 0
      comparedTotalImpressions.value = 0
      impressionsChange.value = 0
      ctr.value = 0
      comparedCtr.value = 0
      ctrChange.value = 0
    }
  },
)

watch(() => [props.startDate, props.endDate], handleDateChange)
watch(
  () => [props.previousStartDate, props.previousEndDate],
  handlePartialDataChange,
)

// Recompute top-card metrics when device filters change (no refetch)
watch(
  () => props.deviceTypeFilters,
  () => {
    // Recompute row-level CTR so table reflects current device-type filters
    analytics.value = (analytics.value ?? []).map(row => ({
      ...row,
      ctrComputed: computeCtrFromData(row.clickCount, row.viewCount || '0'),
    }))
    updateMetrics(lastCurrentData.value, lastPreviousData.value)
  },
  { deep: true },
)
</script>

<style scoped>
.data-card {
  height: 135px;
}
.data-list :deep(.v-data-table-header__content) {
  font-weight: bold !important;
}
:deep(.v-skeleton-loader__chip) {
  height: 25px;
  margin: 0 !important;
}
:deep(.v-skeleton-loader__text) {
  height: 19px;
  margin: 0 !important;
}
.data-card__value-title {
  color: #666;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.75;
}
.data-card__value {
  font-size: 2.5em;
  font-weight: bold;
  line-height: 1em;
  margin-top: 2px;
}
.data-card__compared-value {
  color: orange;
}
.data-card__value-skeleton {
  margin-top: 6px;
}
.data-card__stat {
  display: block;
  line-height: 1.25em;
  margin-top: 10px;
}
.data-card__stat-skeleton {
  margin-top: 16px;
}

/* Disabled placement row styling */
.disabled-placement-row {
  background-color: rgba(0, 0, 0, 0.03) !important;
  border-left: 3px solid #ff5252 !important;
}

.disabled-placement-row:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

.disabled-placement-row td {
  color: rgba(0, 0, 0, 0.7) !important;
}

.non-clickable-row {
  cursor: default;
}

.non-clickable-row:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.cursor-pointer {
  cursor: pointer;
}

/* Flip card styles */
.flip-card {
  perspective: 1000px;
  height: 135px; /* match .data-card height to prevent layout shift */
}
.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.4s ease;
}
.flip-card-inner.flipped {
  transform: rotateY(180deg);
}
.flip-card-face {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.flip-card-back {
  transform: rotateY(180deg);
}

/* Ensure v-card fills the flip container height */
.flip-card .v-card {
  height: 100%;
}
</style>
